import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Button
} from '@mui/material';
import {
  Home,
  Dashboard,
  Person,
  AdminPanelSettings,
  Group,
  Store,
  Add,
  List as ListIcon,
  Logout,
  ExpandLess,
  ExpandMore,
  Storefront,
  PersonAdd,
  People,
  ManageAccounts,
  Receipt,
  ShoppingCart,
  Notifications,
  LocalOffer,
  Schedule,
  LocalShipping,
  Category,
  Assessment,
  TwoWheeler,
  GpsFixed,
  Assignment,
  Route as RouteIcon,
  MoreHoriz,
  Error,
  SystemUpdateAlt,
  Web
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

const DRAWER_WIDTH = 240;

const Sidebar = () => {
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = React.useState({});

  const handleLogout = () => {
    actions.logout();
    navigate('/');
    actions.showSnackbar('Logged out successfully', 'success');
  };

  const handleMenuToggle = (path) => {
    setExpandedMenus(prev => {
      const isCurrentlyExpanded = prev[path];
      
      if (isCurrentlyExpanded) {
        // If currently expanded, just close it
        return {
          ...prev,
          [path]: false
        };
      } else {
        // If currently closed, close all others and open this one
        const newExpandedMenus = {};
        Object.keys(prev).forEach(key => {
          newExpandedMenus[key] = false;
        });
        newExpandedMenus[path] = true;
        return newExpandedMenus;
      }
    });
  };

  const getIcon = (path) => {
    switch (path) {
      case '/':
        return <Home />;
      case '/dashboard':
        return <Dashboard />;
      case '/profile':
        return <Person />;
      case '/admin':
        return <AdminPanelSettings />;
      case '/admin/dashboard':
        return <Dashboard />;
      case '/admin/orders':
        return <Receipt />;
      case '/admin/users':
        return <ManageAccounts />;
      case '/super-admin/stores':
        return <Storefront />;
      case '/super-admin/stores/add':
        return <Add />;
      case '/super-admin/stores/list':
        return <Store />;
      case '/super-admin/users':
        return <People />;
      case '/super-admin/users/add':
        return <PersonAdd />;
      case '/super-admin/users/list':
        return <Group />;
      case '/super-admin/orders':
        return <Receipt />;
      case '/super-admin/orders/list':
        return <ShoppingCart />;
      case '/super-admin/products':
        return <ShoppingCart />;
      case '/super-admin/products/add':
        return <Add />;
      case '/super-admin/products/list':
        return <ListIcon />;
      case '/super-admin/products/stock':
        return <ListIcon />;
      case '/super-admin/coupons':
        return <LocalOffer />;
      case '/super-admin/coupons/add':
        return <Add />;
      case '/super-admin/coupons/list':
        return <ListIcon />;
      case '/super-admin/notifications':
        return <Notifications />;
      case '/super-admin/notifications/manage':
        return <Notifications />;
      case '/super-admin/delivery':
        return <LocalShipping />;
      case '/super-admin/delivery/time-slots':
        return <Schedule />;
      case '/super-admin/delivery/orders':
        return <Assignment />;
      case '/super-admin/delivery/routes':
        return <RouteIcon />;
      case '/super-admin/delivery/riders':
        return <TwoWheeler />;
      case '/super-admin/delivery/live-tracking':
        return <GpsFixed />;
      case '/super-admin/delivery/rider-app-updates':
        return <SystemUpdateAlt />;
      case '/admin/delivery':
        return <LocalShipping />;
      case '/admin/delivery/orders':
        return <Assignment />;
      case '/admin/delivery/routes':
        return <RouteIcon />;
      case '/admin/delivery/riders':
        return <TwoWheeler />;
      case '/admin/delivery/live-tracking':
        return <GpsFixed />;
      case '/super-admin/categories/manage':
        return <Category />;
      case '/admin/reports':
        return <Assessment />;
      case '/admin/reports/sales':
        return <Assessment />;
      case '/super-admin/reports':
        return <Assessment />;
      case '/super-admin/reports/sales':
        return <Assessment />;
      case '/super-admin/others':
        return <MoreHoriz />;
      case '/super-admin/focus-errors':
        return <Error />;
      case '/super-admin/app-website':
        return <Web />;
      case '/super-admin/app-website/banners':
        return <Web />;
      case '/super-admin/app-website/popup-banners':
        return <Web />;
      case '/super-admin/app-website/notifications':
        return <Notifications />;
      default:
        return <Dashboard />;
    }
  };

  const navigationItems = state.isAuthenticated 
    ? appConfig.navigation.main.filter(item => {
        // Filter out public items
        if (item.public) return false;
        // For store_admin users, hide the main dashboard since they have their own admin dashboard
        if (state.user?.role === 'store_admin' && item.path === '/dashboard') return false;
        return true;
      })
    : [];

  const adminItems = state.isAuthenticated && (state.user?.role === 'admin' || state.user?.role === 'store_admin')
    ? appConfig.navigation.admin 
    : [];

  const superAdminItems = state.isAuthenticated && (state.user?.role === 'super_admin' || state.user?.role === 'super-admin')
    ? appConfig.navigation.superAdmin 
    : [];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
        },
      }}
    >
      <Box sx={{ 
        overflow: 'auto', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        /* Hide scrollbar for Chrome, Safari and Opera */
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        /* Hide scrollbar for IE, Edge and Firefox */
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        {/* Logo/Brand */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Box
            component={Link}
            to="/"
            sx={{ 
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >
            <img 
              src={require('../../assets/logo.png')} 
              alt="Logo" 
              style={{
                height: '40px',
                width: 'auto',
                maxWidth: '100%'
              }}
              onError={(e) => {
                // Fallback to pilot.png if logo.png doesn't exist
                e.target.src = require('../../assets/pilot.png');
              }}
            />
          </Box>
        </Box>

        <Divider />

        {state.isAuthenticated && (
          <>

            {/* Main Navigation */}
            <List sx={{ flex: 1 }}>
              {navigationItems.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ px: 1, py: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={location.pathname === item.path}
                    sx={{
                      borderRadius: '15px',
                      mx: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '15px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.contrastText',
                        },
                      },
                      '&:hover': {
                        borderRadius: '15px',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getIcon(item.path)}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '13px'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {/* Admin Section */}
              {adminItems.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <ListItem>
                    <Typography variant="overline" color="text.secondary">
                      {state.user?.role === 'store_admin' ? 'Store Admin' : 'Admin'}
                    </Typography>
                  </ListItem>
                  {adminItems.map((item) => (
                    <div key={item.path}>
                      <ListItem disablePadding sx={{ px: 1, py: 0.5 }}>
                        <ListItemButton
                          component={item.children ? 'div' : Link}
                          to={item.children ? undefined : item.path}
                          onClick={item.children ? () => handleMenuToggle(item.path) : undefined}
                          selected={!item.children && location.pathname === item.path}
                          sx={{
                            borderRadius: '15px',
                            mx: 1,
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              borderRadius: '15px',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'primary.contrastText',
                              },
                            },
                            '&:hover': {
                              borderRadius: '15px',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getIcon(item.path)}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: '13px'
                            }}
                          />
                          {item.children && (
                            expandedMenus[item.path] ? <ExpandLess /> : <ExpandMore />
                          )}
                        </ListItemButton>
                      </ListItem>
                      
                      {/* Submenu items */}
                      {item.children && expandedMenus[item.path] && (
                        <List sx={{ pl: 2 }}>
                          {item.children.map((child) => (
                            <ListItem key={child.path} disablePadding sx={{ px: 1, py: 0.5 }}>
                              <ListItemButton
                                component={Link}
                                to={child.path}
                                selected={location.pathname === child.path}
                                sx={{
                                  borderRadius: '15px',
                                  mx: 1,
                                  '&.Mui-selected': {
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    borderRadius: '15px',
                                    '&:hover': {
                                      backgroundColor: 'primary.dark',
                                    },
                                    '& .MuiListItemIcon-root': {
                                      color: 'primary.contrastText',
                                    },
                                  },
                                  '&:hover': {
                                    borderRadius: '15px',
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  {getIcon(child.path)}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={child.title}
                                  primaryTypographyProps={{
                                    fontSize: '13px'
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Super Admin Section */}
              {superAdminItems.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <ListItem>
                    <Typography variant="overline" color="text.secondary">
                      Super Admin
                    </Typography>
                  </ListItem>
                  {superAdminItems.map((item) => (
                    <div key={item.path}>
                      <ListItem disablePadding sx={{ px: 1, py: 0.5 }}>
                        <ListItemButton
                          onClick={() => item.children ? handleMenuToggle(item.path) : navigate(item.path)}
                          selected={location.pathname === item.path}
                          sx={{
                            borderRadius: '15px',
                            mx: 1,
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              borderRadius: '15px',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'primary.contrastText',
                              },
                            },
                            '&:hover': {
                              borderRadius: '15px',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getIcon(item.path)}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: '13px'
                            }}
                          />
                          {item.children && (
                            expandedMenus[item.path] ? <ExpandLess /> : <ExpandMore />
                          )}
                        </ListItemButton>
                      </ListItem>
                      
                      {/* Submenu items */}
                      {item.children && expandedMenus[item.path] && (
                        <List sx={{ pl: 2 }}>
                          {item.children.map((child) => (
                            <ListItem key={child.path} disablePadding sx={{ px: 1, py: 0.5 }}>
                              <ListItemButton
                                component={Link}
                                to={child.path}
                                selected={location.pathname === child.path}
                                sx={{
                                  borderRadius: '15px',
                                  mx: 1,
                                                                      '&.Mui-selected': {
                                      backgroundColor: 'primary.main',
                                      color: 'primary.contrastText',
                                      borderRadius: '15px',
                                      '&:hover': {
                                        backgroundColor: 'primary.dark',
                                      },
                                      '& .MuiListItemIcon-root': {
                                        color: 'primary.contrastText',
                                      },
                                    },
                                  '&:hover': {
                                    borderRadius: '15px',
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  {getIcon(child.path)}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={child.title}
                                  primaryTypographyProps={{
                                    fontSize: '13px'
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </div>
                  ))}
                </>
              )}

              {/* Profile Section */}
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding sx={{ px: 1, py: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to="/profile"
                  selected={location.pathname === '/profile'}
                  sx={{
                    borderRadius: '15px',
                    mx: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: '15px',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                    },
                    '&:hover': {
                      borderRadius: '15px',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {getIcon('/profile')}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Profile"
                    primaryTypographyProps={{
                      fontSize: '13px'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>

            {/* Logout Button */}
            <Box sx={{ p: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<Logout />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export { DRAWER_WIDTH };
export default Sidebar; 